import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer } from 'aha-components';
import {
  isMobile,
  getAuthorizeError,
  getUpdateTypeFromUrlSearch,
  getUpdateReasonFromUrlSearch,
  getShowModalAnalysisFromUrlSearch,
  getShowModalConfirmDealPriceFromUrlSearch,
  getDebugger,
  getOrderId,
  getAuthorizeErrorMessage
} from '@/biz/tool';
import WebModal from './Modal';
import BottomDrawer from './BottomDrawer';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import ModalAnalysisBreadcrumb from './ModalAnalysisBreadcrumb';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { PlatformIconComponent } from '@/biz/platform';
import {
  RightIcon,
  SuccessIcon,
  AuthorizeErrorIcon,
  ShowImgIcon,
  CloseIcon,
  ScreenshotsIcon
} from '@/components/common/icon';
import toast from 'react-hot-toast';
import { useOrderListStore } from '@/hooks/useOrderListStore';
import ModalAnalysisUploadScreenshotsContent from './ModalAnalysisUploadScreenshotsContent';
import { ConnectBtn } from './ModalSelectPlatformContent';
import { OrderStatus } from '@/interface/order';
import { MOBILE_DRAWER_HEIGHT } from '@/utils/viewportHeight';

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    showModalAnalysis,
    setShowModalAnalysis,
    setShowModalMissOut,
    setShowModalSelectInvitedOrderByNoMatch,
    setShowModalConfirmDealPrice
  } = useWebModalStore();
  const { reQueryOrderListData } = useOrderListStore();
  const [showUploadScreenshotsComponent, setShowUploadScreenshotsComponent] =
    useState(false);

  useEffect(() => {
    if (
      window?.location?.pathname === '/clerk' ||
      window?.location?.pathname.includes('/auth/')
    ) {
      return;
    }
    // 获取 ?orderId=xxx 和 ?a=xxx&orderId=xxx 的情况
    const updateType = getUpdateTypeFromUrlSearch();
    const updateReason = getUpdateReasonFromUrlSearch();
    const AuthorizeError = getAuthorizeError();
    const AuthorizeErrorMessage = getAuthorizeErrorMessage();
    const urlShowModalAnalysis = getShowModalAnalysisFromUrlSearch();
    const urlShowModalConfirmDealPrice =
      getShowModalConfirmDealPriceFromUrlSearch();

    if (AuthorizeError === 'true') {
      if (updateType === 'cancel_order') {
        getOrderListAndShowInvited();
      } else {
        //
        setShowModalAnalysis(true);
        //
        if (
          updateReason === 'mismatch_audience' ||
          updateReason === 'content_check_failed'
        ) {
          //
        } else if (AuthorizeErrorMessage) {
          toast.custom(
            <div className="px-4 py-3 bg-[#ffebe5] text-[#d43710] rounded-lg flex justify-start items-center gap-2">
              <AuthorizeErrorIcon className="w-6 h-6" />
              {AuthorizeErrorMessage || updateReason}
            </div>,
            {
              duration: 3000
            }
          );
        }
      }

      // 清除 url 中的 AuthorizeError
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (urlShowModalAnalysis === 'true') {
      setShowModalAnalysis(true);
      window.history.replaceState(null, '', window.location.pathname);
    } else if (urlShowModalConfirmDealPrice === 'true') {
      setShowModalConfirmDealPrice(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const getOrderListAndShowInvited = async () => {
    // 请求 invited 订单
    setShowModalSelectInvitedOrderByNoMatch(true);
    await reQueryOrderListData();
    // end 请求 invited 订单
  };

  const UploadScreenshotsCard = () => {
    return (
      <div
        className="w-full p-4 border border-[#e8e8ec] hover:border-theme-color rounded-2xl justify-between items-center flex flex-col cursor-pointer"
        onClick={() => {
          setShowUploadScreenshotsComponent(true);
        }}
      >
        <div className="flex w-full h-8 justify-between items-center">
          <ScreenshotsIcon className="w-6 h-6" />
          <RightIcon className="w-5 h-5" />
        </div>
        <div className="mt-4 mb-2 self-stretch justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('modal.analysis.upload_screenshots_title', {
            defaultValue: 'Upload screenshots'
          })}
        </div>

        <div className="self-stretch justify-start text-[#1c2024] text-sm font-normal font-['Plus_Jakarta_Sans'] leading-snug">
          {t('modal.analysis.upload_screenshots_desc', {
            defaultValue:
              "Navigate to your channel's analytics dashboard and take screenshots of the audience / viewer insights"
          })}
        </div>
      </div>
    );
  };

  const ConnectYourChannelCard = () => {
    return (
      <div className="w-full p-4 border border-[#e8e8ec] hover:border-theme-color rounded-2xl justify-between items-center flex flex-col cursor-pointer">
        <div className="w-full flex justify-between items-center">
          <div className="h-12 justify-start items-center gap-2 flex">
            <div className="justify-start items-center gap-3 flex relative">
              <img
                // alt="avatar"
                className="w-12 h-12 rounded-full"
                src={detailData?.influencer?.profilePictureLink}
              />
              <div className="absolute right-0 bottom-0">
                <PlatformIconComponent
                  platform={detailData?.order?.platform}
                  className="w-4 h-4"
                />
              </div>
            </div>
            <div className="grow shrink basis-0 flex-col justify-center items-start gap-1 inline-flex">
              <div className="text-zinc-950 text-base font-semibold font-['Plus Jakarta Sans']">
                {detailData?.influencer?.name}
              </div>
              <div className="self-stretch text-[#8b8d98] text-sm font-normal font-['Inter']">
                {detailData?.influencer?.username
                  ? detailData.influencer.username.startsWith('@')
                    ? detailData.influencer.username
                    : `@${detailData.influencer.username}`
                  : ''}
              </div>
            </div>
          </div>
          {!detailData?.influencer?.isAuthorized ? (
            <RightIcon className="w-5 h-5 text-[#60646C]" />
          ) : (
            <div className="flex items-center gap-2">
              <SuccessIcon className="w-5 h-5" />
              <div className="text-[#287f2e] text-sm font-medium font-['Inter'] leading-snug">
                {t('modal.analysis.success_status', {
                  defaultValue: 'Success'
                })}
              </div>
            </div>
          )}
        </div>
        {/*  */}
        <div className="mt-4 mb-2 self-stretch justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
          {t('modal.analysis.connect_channel_title', {
            defaultValue: 'Connect your channel'
          })}
        </div>

        <div className="self-stretch justify-start text-[#1c2024] text-sm font-normal font-['Plus_Jakarta_Sans'] leading-snug">
          {t('modal.analysis.connect_channel_desc', {
            defaultValue:
              'Directly connect your channel to provide your audience data'
          })}
        </div>
      </div>
    );
  };

  const AnalysisContent = () => {
    const [showImg, setShowImg] = useState(false);

    return (
      <div className="relative w-full flex flex-col items-start gap-6 flex-1 min-h-0">
        <ModalShowImg isOpen={showImg} setIsOpen={setShowImg} />
        <div className="shrink-0 w-full">
          <ModalAnalysisBreadcrumb index={0} />
        </div>
        <div className="w-full flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
          <a className="group">
            {t('modal.analysis.audience_data_help', {
              defaultValue:
                'Sharing audience data helps brands better understand your audience and expedite the review process, ensuring that promoted content is accurately targeted.'
            })}
            <span
              className="text-[#0C67E6] hover:underline cursor-pointer font-normal"
              onClick={() => {
                setShowImg(true);
              }}
            >
              <ShowImgIcon className="ml-1 mr-[2px] w-5 h-5 shrink-0 inline-block align-text-bottom group-hover:underline" />
              {t('modal.analysis.see_audience_info', {
                defaultValue:
                  "See how audience information is displayed on the brand's side"
              })}
            </span>
          </a>
          <div className="self-stretch justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
            {t('modal.analysis.select_method', {
              defaultValue: 'Select method'
            })}
          </div>
          <div
            className={`
            ${isMobile() ? 'grid grid-rows-2 gap-4' : 'grid grid-cols-1 gap-4'}
          `}
          >
            {/* 上传截图 */}
            <UploadScreenshotsCard />
            {/* 连接你的频道 */}
            {/* <ConnectBtn
              validateOrderId={true}
              platform={{ platform: detailData?.order?.platform, id: null }}
              isConnected={false}
              urlShowModalAnalysis={true}
              contentRender={<ConnectYourChannelCard />}
            /> */}
          </div>
        </div>
        <div
          className={`bg-white w-full flex items-center gap-3 border-t border-solid border-[#e8e8ec] pt-4 shrink-0
          ${isMobile() ? 'justify-center pb-[calc(16px+env(safe-area-inset-bottom,0px))]' : 'justify-end'}
          `}
        >
          <Button
            className="!w-[160px] !h-10"
            type="default"
            onClick={() => {
              setShowModalAnalysis(false);
              setShowModalMissOut(true);
            }}
          >
            {t('modal.analysis.cancel_btn', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            className="!w-[160px] !h-10"
            type="primary"
            disabled={
              getDebugger() === 'true'
                ? false
                : !detailData?.influencer?.isAuthorized
            }
            onClick={() => {
              setShowModalAnalysis(false);
              setShowModalConfirmDealPrice(true);
            }}
          >
            {t('modal.analysis.next_btn', { defaultValue: 'Next' })}
          </Button>
        </div>
      </div>
    );
  };

  if (!detailData?.order?.status.includes(OrderStatus.Invited)) {
    return null;
  }

  return (
    <Drawer
      title={t('modal.analysis.share_insights_title', {
        defaultValue: 'Share audience insights'
      })}
      open={showModalAnalysis}
      onClose={() => setShowModalAnalysis(false)}
      bodyStyle={{
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
      height={isMobile() ? MOBILE_DRAWER_HEIGHT : undefined}
      placement={isMobile() ? 'bottom' : 'right'}
      width={isMobile() ? '100%' : 810}
    >
      {showUploadScreenshotsComponent ? (
        <ModalAnalysisUploadScreenshotsContent
          setShowUploadScreenshotsComponent={setShowUploadScreenshotsComponent}
        />
      ) : (
        <AnalysisContent />
      )}
    </Drawer>
  );
};

export default ModalShowAnInterest;

const ModalShowImg = ({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) => {
  const AudienceInsightsImage = () => {
    return (
      <div className="max-w-full">
        <img
          alt="audience-insights"
          src={'/audience-insights.png'}
          className="w-full h-auto object-contain"
        />
      </div>
    );
  };

  return (
    <WebModal
      visible={isOpen}
      canClose={true}
      handleClose={() => setIsOpen(false)}
      isShowFooter={false}
      isShowHeader={false}
      width={isMobile() ? 375 : undefined}
      className={`!z-[9999]
        ${isMobile() ? 'p-0' : 'p-0'}
        `}
    >
      <div className="absolute -top-12 right-2 rounded-full p-2 cursor-pointer hover:bg-gray-200">
        <CloseIcon className="w-6 h-6" onClick={() => setIsOpen(false)} />
      </div>
      <AudienceInsightsImage />
    </WebModal>
  );
};
